import {all, call, delay, put, select, takeLatest} from '@redux-saga/core/effects';
import AgencyAdministrationRouteConstants from 'AgencyAdministration/AgencyAdministrationRouteConstants';
import {getAgentFilter} from 'AgencyAdministration/components/Agents';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import accountApi from 'Agent/Accounts/api/accountApi';
import {Account} from 'Agent/Accounts/models/Account';
import {Policy} from 'Agent/Policies/api/apiTypes';
import policyApi from 'Agent/Policies/api/policyApi';
import {PolicyFilter} from 'Agent/Policies/ducks/policy';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import {push} from 'connected-react-router';
import {
  loadResults,
  searchAccounts,
  searchAccountsByNumber,
  SearchActionType,
  searchAgents,
  searchAgentsByLastName,
  searchPolicies,
  searchPoliciesByLast4OfOwnerSSN,
  searchPoliciesByLast4OfSSN,
  searchPoliciesByPayorName,
  searchPoliciesByPolicyNumber,
  SearchResult,
} from 'Core/ducks/search';
import {createSnackbar} from 'Core/ducks/snackbar';
import {Async, AsyncResult, fromResult, isLoaded, Loading} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {ReducerState} from 'rootReducer';
import {Scope} from 'Shared/constants/Scope';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {
  getEisFlagFromLinks,
  getEisGroupNumberFromLinks,
  getEisProductCdFromLinks,
} from 'Shared/utilities/DataSourceUtility';
import {prop} from 'Shared/utilities/prop';
import agentApi from 'Users/api/agentApi';
import {impersonateAgent} from 'Users/ducks/agentAdministration';
import {EmptyAgentUserFilter} from 'Users/ducks/agentUser';
import {Agent, getFullName} from 'Users/models/Agent';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {ValidAgentNumber} from 'Users/models/AgentNumber';
import {UserClaims} from 'Users/models/Claims';
import agencyFilterSelector from 'Users/selectors/selectAgencyFilters';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';

const MAX_NUMBER_OF_RESULTS = 5;

function* resolveResults(results: AsyncResult<SearchResult[]>) {
  if (E.isRight(results)) {
    yield put(loadResults(fromResult(results.right)));
  } else {
    yield put(loadResults(results.left));
  }
}

function* searchPoliciesApi(action: ReturnType<typeof searchPolicies>) {
  yield delay(500);
  yield put(loadResults(new Loading()));
  const {insuredName} = action.payload;

  const agencyFilters: O.Option<AgencyFilters> = yield select(agencyFilterSelector);

  if (O.isSome(agencyFilters)) {
    const results: AsyncResult<Page<Policy>> = yield call(
      policyApi.searchPolicies,
      agencyFilters.value.agentNumbers,
      agencyFilters.value.isNewYork,
      agencyFilters.value.encryptedIdentifier,
      {
        insuredName,
        pageSize: MAX_NUMBER_OF_RESULTS,
      } as PolicyFilter
    );

    yield resolveResults(
      pipe(
        results,
        E.map(policyEither =>
          policyEither.result.map(
            policy =>
              ({
                label: `${policy.policyNumber} - ${policy.insuredName}`,
                selectRoute: push(
                  PolicyRouteConstants.POLICY_DETAILS(
                    policy.id!,
                    policy.policyNumber!,
                    getEisFlagFromLinks(policy.links)
                  )
                ),
                viewMoreRoute: PolicyRouteConstants.POLICIES_BY_INSURED_NAME(action.payload.insuredName),
                value: policy.id,
              } as SearchResult)
          )
        )
      )
    );
  }
}

function* searchAccountsApi(action: ReturnType<typeof searchAccounts>) {
  yield delay(500);
  yield put(loadResults(new Loading()));

  const currentAgentNumbers: ValidAgentNumber[] = yield select(
    (state: ReducerState) => state.user.profile.currentAgentNumbers
  );
  const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
    (state: ReducerState) => state.user.profile.selectedAgency
  );

  if (O.isSome(selectedAgency)) {
    let bodyParams = new URLSearchParams();
    bodyParams.append('agentNumbers', currentAgentNumbers.map(prop('value')).join(','));
    bodyParams.append('encryptedIdentifier', selectedAgency.value.encryptedIdentifier);
    bodyParams.append('isNewYork', JSON.stringify(selectedAgency.value.isNewYork));
    bodyParams.append('name', action.payload.name);
    const results: AsyncResult<Page<Account>> = yield call(accountApi.getAccounts, bodyParams);
    yield resolveResults(
      pipe(
        results,
        E.map(accountEither =>
          accountEither.result.slice(0, 5).map(
            account =>
              ({
                label: account.name,
                selectRoute: push(
                  AccountRouteConstants.ACCOUNT_DETAILS(
                    account.id!,
                    account.number!,
                    getEisFlagFromLinks(account.links),
                    getEisGroupNumberFromLinks(account),
                    getEisProductCdFromLinks(account)
                  )
                ),
                viewMoreRoute: AccountRouteConstants.ACCOUNTS_BY_NAME(action.payload.name),
                value: account.id,
              } as SearchResult)
          )
        )
      )
    );
  }
}

function* searchAgentsByLastNameApi(action: ReturnType<typeof searchAgentsByLastName>) {
  yield put(loadResults(new Loading()));

  const claims: Async<UserClaims> = yield select((state: ReducerState) => state.user.profile.userClaims);
  const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
    (state: ReducerState) => state.user.profile.selectedAgency
  );
  const scopes: Scope[] = yield select(selectUserScopes);

  if (isLoaded(claims)) {
    const filter = getAgentFilter(
      EmptyAgentUserFilter,
      claims,
      scopes,
      O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined
    );
    const results: AsyncResult<Page<Agent>> = yield call(agentApi.searchAgentUsers, {
      ...filter,
      lastName: action.payload.lastName,
    });

    yield resolveResults(
      pipe(
        results,
        E.map(agentEither =>
          agentEither.result.slice(0, 5).map(
            agent =>
              ({
                label: getFullName(agent),
                selectRoute: impersonateAgent(claims.Data.userName, agent.userName, [agent.firstName, agent.lastName]),
                viewMoreRoute: AgencyAdministrationRouteConstants.AGENTS_BY_LAST_NAME(action.payload.lastName),
                value: agent.id.toString(),
              } as SearchResult)
          )
        )
      )
    );
  } else {
    yield put(createSnackbar('Unable To Load Agents.'));
  }
}

function* searchAgentsApi(action: ReturnType<typeof searchAgents>) {
  yield put(loadResults(new Loading()));

  const claims: Async<UserClaims> = yield select((state: ReducerState) => state.user.profile.userClaims);
  const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
    (state: ReducerState) => state.user.profile.selectedAgency
  );
  const scopes: Scope[] = yield select(selectUserScopes);

  if (isLoaded(claims)) {
    const filter = getAgentFilter(
      EmptyAgentUserFilter,
      claims,
      scopes,
      O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined
    );
    const results: AsyncResult<Page<Agent>> = yield call(agentApi.searchAgentUsers, {
      ...filter,
      userName: action.payload.name,
    });

    yield resolveResults(
      pipe(
        results,
        E.map(agentEither =>
          agentEither.result.slice(0, 5).map(
            agent =>
              ({
                label: getFullName(agent),
                selectRoute: impersonateAgent(claims.Data.userName, agent.userName, [agent.firstName, agent.lastName]),
                viewMoreRoute: AgencyAdministrationRouteConstants.AGENTS_BY_NAME(action.payload.name),
                value: agent.id.toString(),
              } as SearchResult)
          )
        )
      )
    );
  } else {
    yield put(createSnackbar('Unable To Load Agents.'));
  }
}

function* searchPolicesSaga() {
  yield takeLatest(SearchActionType.SEARCH_POLICES_BY_INSURED_NAME, searchPoliciesApi);
}

function* searchPoliciesByPayorNameSaga() {
  yield takeLatest(SearchActionType.SEARCH_POLICIES_BY_PAYOR_NAME, function*(
    action: ReturnType<typeof searchPoliciesByPayorName>
  ) {
    yield delay(500);
    yield put(loadResults(new Loading()));
    const {name} = action.payload;

    const agencyFilters: O.Option<AgencyFilters> = yield select(agencyFilterSelector);

    if (O.isSome(agencyFilters)) {
      const results: AsyncResult<Page<Policy>> = yield call(
        policyApi.searchPolicies,
        agencyFilters.value.agentNumbers,
        agencyFilters.value.isNewYork,
        agencyFilters.value.encryptedIdentifier,
        {
          payorName: name,
          pageSize: MAX_NUMBER_OF_RESULTS,
        } as PolicyFilter
      );

      yield resolveResults(
        pipe(
          results,
          E.map(policyEither =>
            policyEither.result.map(
              policy =>
                ({
                  label: `${policy.policyNumber} - ${policy.insuredName}`,
                  selectRoute: push(
                    PolicyRouteConstants.POLICY_DETAILS(
                      policy.id!,
                      policy.policyNumber!,
                      getEisFlagFromLinks(policy.links)
                    )
                  ),
                  viewMoreRoute: PolicyRouteConstants.POLICIES_BY_PAYOR_NAME(name),
                  value: policy.id,
                } as SearchResult)
            )
          )
        )
      );
    }
  });
}

function* searchPoliciesByPolicyNumberSaga() {
  yield takeLatest(SearchActionType.SEARCH_POLICIES_BY_POLICY_NUMBER, function*(
    action: ReturnType<typeof searchPoliciesByPolicyNumber>
  ) {
    yield delay(500);
    yield put(loadResults(new Loading()));
    const {policyNumber, isExact} = action.payload;

    const agencyFilters: O.Option<AgencyFilters> = yield select(agencyFilterSelector);

    if (O.isSome(agencyFilters)) {
      const results: AsyncResult<Page<Policy>> = yield call(
        policyApi.searchPolicies,
        agencyFilters.value.agentNumbers,
        agencyFilters.value.isNewYork,
        agencyFilters.value.encryptedIdentifier,
        {
          policyNumber,
          pageSize: MAX_NUMBER_OF_RESULTS,
          isExact,
        } as PolicyFilter
      );

      yield resolveResults(
        pipe(
          results,
          E.map(policyEither =>
            policyEither.result.map(
              policy =>
                ({
                  label: `${policy.policyNumber} - ${policy.insuredName}`,
                  selectRoute: push(
                    PolicyRouteConstants.POLICY_DETAILS(
                      policy.id!,
                      policy.policyNumber!,
                      getEisFlagFromLinks(policy.links)
                    )
                  ),
                  viewMoreRoute: PolicyRouteConstants.POLICIES_BY_POLICY_NUMBER(policyNumber),
                  value: policy.id,
                } as SearchResult)
            )
          )
        )
      );
    }
  });
}

function* searchPoliciesByLast4OfOwnerSSNSaga() {
  yield takeLatest(SearchActionType.SEARCH_POLICES_BY_LAST_4_OF_OWNER_SSN, function*(
    action: ReturnType<typeof searchPoliciesByLast4OfOwnerSSN>
  ) {
    yield delay(500);
    yield put(loadResults(new Loading()));
    const {ssn} = action.payload;

    const agencyFilters: O.Option<AgencyFilters> = yield select(agencyFilterSelector);

    if (O.isSome(agencyFilters)) {
      const results: AsyncResult<Page<Policy>> = yield call(
        policyApi.searchPolicies,
        agencyFilters.value.agentNumbers,
        agencyFilters.value.isNewYork,
        agencyFilters.value.encryptedIdentifier,
        {
          ownerLast4OfSSN: ssn,
          pageSize: MAX_NUMBER_OF_RESULTS,
        } as PolicyFilter
      );

      yield resolveResults(
        pipe(
          results,
          E.map(policyEither =>
            policyEither.result.map(
              policy =>
                ({
                  label: `${policy.policyNumber} - ${policy.insuredName}`,
                  selectRoute: push(
                    PolicyRouteConstants.POLICY_DETAILS(
                      policy.id!,
                      policy.policyNumber!,
                      getEisFlagFromLinks(policy.links)
                    )
                  ),
                  viewMoreRoute: PolicyRouteConstants.POLICIES_BY_LAST_4_OF_OWNER_SSN(ssn),
                  value: policy.id,
                } as SearchResult)
            )
          )
        )
      );
    }
  });
}

function* searchPoliciesByLast4SSNSaga() {
  yield takeLatest(SearchActionType.SEARCH_POLICES_BY_LAST_4_OF_SSN, function*(
    action: ReturnType<typeof searchPoliciesByLast4OfSSN>
  ) {
    yield delay(500);
    yield put(loadResults(new Loading()));
    const {ssn} = action.payload;

    const agencyFilters: O.Option<AgencyFilters> = yield select(agencyFilterSelector);

    if (O.isSome(agencyFilters)) {
      const results: AsyncResult<Page<Policy>> = yield call(
        policyApi.searchPolicies,
        agencyFilters.value.agentNumbers,
        agencyFilters.value.isNewYork,
        agencyFilters.value.encryptedIdentifier,
        {
          last4PayorNumber: ssn,
          pageSize: MAX_NUMBER_OF_RESULTS,
        } as PolicyFilter
      );

      yield resolveResults(
        pipe(
          results,
          E.map(policyEither =>
            policyEither.result.map(
              policy =>
                ({
                  label: `${policy.policyNumber} - ${policy.insuredName}`,
                  selectRoute: push(
                    PolicyRouteConstants.POLICY_DETAILS(
                      policy.id!,
                      policy.policyNumber!,
                      getEisFlagFromLinks(policy.links)
                    )
                  ),
                  viewMoreRoute: PolicyRouteConstants.POLICIES_BY_LAST_4_OF_SSN(ssn),
                  value: policy.id,
                } as SearchResult)
            )
          )
        )
      );
    }
  });
}

function* searchAccountsByNumberSaga() {
  yield takeLatest(SearchActionType.SEARCH_ACCOUNTS_BY_NUMBER, function*(
    action: ReturnType<typeof searchAccountsByNumber>
  ) {
    yield delay(500);
    const {accountNumber} = action.payload;
    yield put(loadResults(new Loading()));
    const agencyFilters: O.Option<AgencyFilters> = yield select(agencyFilterSelector);

    if (O.isSome(agencyFilters)) {
      let bodyParams = new URLSearchParams();
      bodyParams.append('agentNumbers', agencyFilters.value.agentNumbers.join(','));
      bodyParams.append('encryptedIdentifier', agencyFilters.value.encryptedIdentifier);
      bodyParams.append('isNewYork', JSON.stringify(agencyFilters.value.isNewYork));
      bodyParams.append('number', accountNumber);
      const results: AsyncResult<Page<Account>> = yield call(accountApi.getAccounts, bodyParams);

      yield resolveResults(
        pipe(
          results,
          E.map(accountEither =>
            accountEither.result.slice(0, 5).map(
              account =>
                ({
                  label: account.name,
                  selectRoute: push(
                    AccountRouteConstants.ACCOUNT_DETAILS(
                      account.id!,
                      account.number!,
                      getEisFlagFromLinks(account.links),
                      getEisGroupNumberFromLinks(account),
                      getEisProductCdFromLinks(account)
                    )
                  ),
                  viewMoreRoute: AccountRouteConstants.ACCOUNTS_BY_ACCOUNT_NUMBER(accountNumber),
                  value: account.id,
                } as SearchResult)
            )
          )
        )
      );
    }
  });
}

function* searchAccountsSaga() {
  yield takeLatest(SearchActionType.SEARCH_ACCOUNTS, searchAccountsApi);
}

function* searchAgentsSaga() {
  yield takeLatest(SearchActionType.SEARCH_AGENTS, searchAgentsApi);
}

function* searchAgentsByLastNameSaga() {
  yield takeLatest(SearchActionType.SEARCH_AGENTS_BY_LAST_NAME, searchAgentsByLastNameApi);
}

export default function*() {
  yield all([
    searchPoliciesByPolicyNumberSaga(),
    searchPoliciesByPayorNameSaga(),
    searchPoliciesByLast4SSNSaga(),
    searchPoliciesByLast4OfOwnerSSNSaga(),
    searchAccountsByNumberSaga(),
    searchPolicesSaga(),
    searchAccountsSaga(),
    searchAgentsSaga(),
    searchAgentsByLastNameSaga(),
  ]);
}
